import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';

import { AppContext } from 'app/app-config';
import { AuthService, JwtCirPayload } from 'app/home/common/services/legacy/auth/auth.service';

export type LegacyApiAddress = {
  street_address: string;
  unit_number?: string;
};

type RedirectTo = { redirect_to?: string };

export type LegacyApiAddressResponse = {
  address?: {
    id: number;
    verified: boolean;
    latitude: number;
    longitude: number;
    standardized_address: string;
    state: string;
    state_full: string;
  };
  authentication?: {
    token: string;
    expires: number;
  };
  resumable?: boolean;
} & RedirectTo;

export type DecodedAuthentication = {
  token: string;
  expires: number;
  decoded: JwtCirPayload | undefined;
};

export type DecodedLegacyApiAddressResponse = RedirectTo &
  Omit<LegacyApiAddressResponse, 'authentication'> & {
    authentication?: DecodedAuthentication;
  };

@Injectable({
  providedIn: 'root',
})
export class LegacyCreateCirService {
  @AppContext() private _appContext!: AppContext;

  private httpClient = inject(HttpClient);
  private authService = inject(AuthService);

  private get url() {
    return `${this._appContext.apiRoot.quoteV1}/addresses`;
  }

  getAddress(): Observable<LegacyApiAddressResponse> {
    return this.httpClient.get<LegacyApiAddressResponse>(this.url);
  }

  getCIR(address: LegacyApiAddress): Observable<DecodedLegacyApiAddressResponse> {
    if (!address.unit_number) {
      address.unit_number = '';
    }
    return this.httpClient.post(this.url, address).pipe(
      map((response: LegacyApiAddressResponse) => {
        if (response.redirect_to) {
          return response as RedirectTo;
        }
        if (response.authentication) {
          const decodedToken = this.authService.decodeCirJwt(response.authentication.token);
          return {
            ...response,
            ...{
              authentication: {
                decoded: decodedToken ?? undefined,
                token: response.authentication?.token,
                expires: response.authentication?.expires,
              },
            },
          };
        }
        return { redirect_to: '404' } as RedirectTo;
      }),
    );
  }
}
